import { FIFTHTEEN_SECONDS_IN_MS, FIVE_MINUTES_IN_MS } from '@allurion/utils';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import { InsightsApi, buildUrl } from 'src/api/Api';
import { MessageNotification } from 'src/domain/Provider';
import { ClinicSettingsFeatures } from 'src/domain/settings';

import { useCurrentUser } from './useCurrentUser';
import { useUserClinicsSettings } from './useUserClinicsSettings';

type APIResponse = {
  notifications: MessageNotification[];
  next_page: boolean;
};

export function useHasMessageNotifications() {
  const { user } = useCurrentUser();
  const [isPhasedOut, setIsPhasedOut] = useState(false);

  const { data, isValidating } = useSWR(
    isPhasedOut && user.username && ['/auth/message-notifications', user.username],
    ([, username]: [string, string]) =>
      InsightsApi.get<APIResponse>(`/auth/message-notifications?username=${username}`)
        .then(({ notifications }) => notifications)
        .catch(() => []),
    {
      revalidateOnFocus: false,
      refreshInterval: FIVE_MINUTES_IN_MS,
    }
  );

  useEffect(() => {
    if (isPhasedOut) return;

    const id = setTimeout(() => {
      setIsPhasedOut(true);
    }, FIFTHTEEN_SECONDS_IN_MS);

    return () => clearTimeout(id);
  });

  return {
    data: data && data.length > 0,
    isLoading: isValidating,
  };
}

export function useMessageNotifications() {
  const { user } = useCurrentUser();
  const { hasAccessToFeature } = useUserClinicsSettings();
  const [isUpdating, setIsUpdating] = useState(false);
  const pageSize = 5;

  const shouldFetchNotifications =
    user.sessionToken && hasAccessToFeature(ClinicSettingsFeatures.Messages);

  const { data, error, isValidating, mutate, setSize } = useSWRInfinite(
    getKey,
    (_url) => InsightsApi.get<APIResponse>(_url),
    { revalidateOnFocus: false }
  );

  const removeNotification = async (id: string) => {
    setIsUpdating(true);
    await InsightsApi.delete(`/auth/message-notifications/${id}`);
    await mutate();
    setIsUpdating(false);
  };

  const clearAll = async () => {
    setIsUpdating(true);
    await InsightsApi.delete(`/auth/message-notifications`);
    await mutate();
    setIsUpdating(false);
  };

  const notifications = data?.flatMap((page) => page.notifications ?? []) ?? [];

  return {
    isLoading: isValidating || isUpdating,
    error,
    notifications,
    hasNextPage: data?.[data.length - 1]?.next_page ?? false,
    loadMore() {
      setSize((size) => size + 1);
    },
    removeNotification,
    clearAll,
  };

  function getKey(page: number, previousPageData: APIResponse) {
    if (previousPageData && !previousPageData.next_page) {
      return null; // reached the end
    }

    return (
      shouldFetchNotifications &&
      buildUrl('/auth/message-notifications', {
        page: page + 1,
        pageSize,
      })
    );
  }
}
